import React from 'react';
import {
  IconArrowRight,
  IconBarbell,
  IconChartBar,
  IconTrophy,
  IconUsers,
} from '@tabler/icons-react';

import crest from '@assets/images/tc-gymnasium-logo.png';
import foundersHero from '@assets/images/tc-founders-hero.png';
import wordmark from '@assets/images/tc-gymnasium-wordmark.png';
import firstWorkoutBadge from '@assets/images/achievements/first-workout.png';
import tenWorkoutsBadge from '@assets/images/achievements/ten-workouts.png';
import twentyFiveWorkoutsBadge from '@assets/images/achievements/twenty-five-workouts.png';
import firstPrBadge from '@assets/images/achievements/first-pr.png';

import classes from './Home.module.css';

const institutionalStats = [
  {
    icon: IconBarbell,
    value: 'Est. 2026',
    detail: 'Brooks, Georgia',
  },
  {
    icon: IconUsers,
    value: '2 Founders',
    detail: '8 years apart',
  },
  {
    icon: IconTrophy,
    value: 'Zero Excuses',
    detail: 'Plenty of progress',
  },
  {
    icon: IconChartBar,
    value: 'A Higher Standard',
    detail: 'Always',
  },
];

const pillars = [
  {
    number: '01',
    icon: IconChartBar,
    title: 'Track your work',
    copy: 'Log workouts, set goals, earn badges, and watch progress accumulate.',
    link: 'See how it works',
    href: '#record-book',
  },
  {
    number: '02',
    icon: IconTrophy,
    title: 'Stay motivated',
    copy: 'Achievements, milestones, and a little friendly competition.',
    link: 'View the record book',
    href: '#record-book',
  },
  {
    number: '03',
    icon: IconUsers,
    title: 'A stronger you',
    copy: 'More than just numbers. T&C builds stronger athletes and kinder humans.',
    link: 'About the gymnasium',
    href: '#mission',
  },
];

const achievements = [
  {
    image: firstWorkoutBadge,
    name: 'First Workout',
    note: 'Good work. Just the start.',
  },
  {
    image: tenWorkoutsBadge,
    name: '10 Workouts',
    note: 'Keep going.',
  },
  {
    image: twentyFiveWorkoutsBadge,
    name: '25 Workouts',
    note: 'Locked in.',
  },
  {
    image: firstPrBadge,
    name: 'First PR',
    note: 'New best.',
  },
];

function EnterTheGymLink({ className }) {
  return (
    <a className={className} href="/app">
      Enter the gym
      <IconArrowRight size={16} stroke={2.4} aria-hidden="true" />
    </a>
  );
}

export default function MarketingHome() {
  return (
    <div className={classes.page}>
      <header className={classes.header}>
        <a className={classes.brand} href="#top" aria-label="T&C's Gymnasium home">
          <img src={crest} alt="" />
          <span>
            <strong>T&amp;C’s Gymnasium</strong>
            <small>Discipline builds freedom.</small>
          </span>
        </a>

        <nav className={classes.nav} aria-label="Main navigation">
          <a className={`${classes.navActive} ${classes.navDesktopOnly}`} href="#top">
            Home
          </a>
          <a href="#gymnasium">The Gymnasium</a>
          <a href="/the-founders">The Founders</a>
          <a className={classes.navDesktopOnly} href="#record-book">
            Record Book
          </a>
        </nav>
      </header>

      <main id="top">
        <section className={classes.hero} aria-labelledby="hero-title">
          <div className={classes.heroCopy}>
            <p className={classes.kicker}>
              Strength <span>★</span> Discipline <span>★</span> Progress{' '}
              <span>★</span> Brotherhood
            </p>
            <h1 className={classes.heroTitle} id="hero-title">
              <span className={classes.heroLogoLockup}>
                <img
                  className={classes.heroLogo}
                  src={wordmark}
                  alt="T&C's Gymnasium"
                />
                <span className={classes.heroPossessive} aria-hidden="true">
                  ’s
                </span>
              </span>
            </h1>
            <p className={classes.tagline}>Discipline builds freedom.</p>
            <p className={classes.heroBody}>
              A training space, a tracking system, and a lifelong commitment
              to getting better. Founded by two brothers. Powered by hard
              work, good humor, and a few bad ideas.
            </p>
            <EnterTheGymLink className={classes.heroCta} />
            <p className={classes.microcopy}>Train &nbsp;/&nbsp; Track &nbsp;/&nbsp; Improve</p>
          </div>

          <div className={classes.heroArt} id="founders">
            <p className={`${classes.annotation} ${classes.annotationLeft}`}>
              Work harder.
              <br />
              Together.
            </p>
            <img
              src={foundersHero}
              alt="Tucker and Clark, the founders of T&C's Gymnasium"
            />
            <p className={`${classes.annotation} ${classes.annotationRight}`}>
              Same standards.
              <br />
              Different bedtimes.
            </p>
            <div className={classes.standardBlock}>
              <strong>Better athletes</strong>
              <span>Kinder humans</span>
            </div>
          </div>
        </section>

        <section className={classes.stats} aria-label="Gymnasium facts">
          {institutionalStats.map(({ icon: Icon, value, detail }) => (
            <div className={classes.stat} key={value}>
              <Icon size={28} stroke={1.6} aria-hidden="true" />
              <div>
                <strong>{value}</strong>
                <span>{detail}</span>
              </div>
            </div>
          ))}
        </section>

        <section className={classes.pillars} id="gymnasium" aria-label="What we do">
          {pillars.map(({ number, icon: Icon, title, copy, link, href }) => (
            <article className={classes.pillar} key={title}>
              <div className={classes.pillarIcon}>
                <Icon size={31} stroke={1.8} aria-hidden="true" />
              </div>
              <div className={classes.pillarCopy}>
                <p className={classes.pillarNumber}>{number}</p>
                <h2>{title}</h2>
                <p>{copy}</p>
                <a href={href}>
                  {link}
                  <IconArrowRight size={14} stroke={2.4} aria-hidden="true" />
                </a>
              </div>
            </article>
          ))}
        </section>

        <section className={classes.recordBook} id="record-book">
          <div className={classes.sectionHeading}>
            <div>
              <p>Official honors</p>
              <h2>Hard work looks good on you.</h2>
            </div>
            <a href="/app">
              Enter the record book
              <IconArrowRight size={14} stroke={2.4} aria-hidden="true" />
            </a>
          </div>

          <div className={classes.badgeGrid}>
            {achievements.map((achievement) => (
              <figure className={classes.badge} key={achievement.name}>
                <img src={achievement.image} alt={`${achievement.name} achievement badge`} />
                <figcaption>
                  <strong>{achievement.name}</strong>
                  <span>{achievement.note}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        <section className={classes.mission} id="mission">
          <div className={classes.missionStatement}>
            <p>
              A little
              <br />
              harder
              <br />
              today.
            </p>
            <span>— T&amp;C’s Gymnasium</span>
          </div>
          <div className={classes.missionCopy}>
            <p className={classes.missionKicker}>The mission</p>
            <h2>Building stronger athletes and a brighter tomorrow.</h2>
            <p>
              Through training, discipline, and brotherhood, T&amp;C’s Gymnasium
              exists to help our athletes become the strongest, kindest, most
              capable versions of themselves.
            </p>
            <a href="#gymnasium">
              The Gymnasium
              <IconArrowRight size={14} stroke={2.4} aria-hidden="true" />
            </a>
          </div>
        </section>
      </main>

      <footer className={classes.footer}>
        <a className={classes.brand} href="#top" aria-label="Back to top">
          <img src={crest} alt="" />
          <span>
            <strong>T&amp;C’s Gymnasium</strong>
            <small>Discipline builds freedom.</small>
          </span>
        </a>
        <nav aria-label="Footer navigation">
          <a href="#top">Home</a>
          <a href="#gymnasium">The Gymnasium</a>
          <a href="/the-founders">The Founders</a>
          <a href="#record-book">Record Book</a>
        </nav>
        <EnterTheGymLink className={classes.footerCta} />
        <p className={classes.location}>Est. 2026 · Brooks, GA</p>
        <p className={classes.copyright}>
          © 2026 T&amp;C’s Gymnasium. All rights reserved.
        </p>
        <p className={classes.footerMotto}>Stronger athletes. Kinder humans.</p>
      </footer>
    </div>
  );
}
